/**
 * The layer bytes, the hosting window, and the display URL's lifetime.
 *
 * Extracted from `MaskEditor.tsx` (WS-J J-9 / AUD-2 RC-4) with no change to a single body.
 *
 * What this hook owns:
 *  - the three fill layers (test design, clear-case background, texture): their bytes, names and
 *    file-input refs, and the `apply*` helpers that keep each byte REF and its state in lockstep;
 *  - ED-7's display-URL lifetime — `setDisplayImage` revokes the object URL it replaces and the
 *    unmount effect revokes the last one. AUD-2 found no remaining leak here; this move must not
 *    introduce one, which is why `use-layer-assets.test.ts` opens with exactly that;
 *  - the upload handlers, `pickImageFromUrl`, and ED-6's `layerUploadRef` window — which must
 *    CLOSE on failure too, or every later bake is downgraded to a display-only compose forever;
 *  - `resetBackground` (the "Restore background" revert, with ED-8's matte stash);
 *  - the layer-refs restore effect, whose ordering (the synchronous state batch FIRST, the awaited
 *    byte fetches AFTER) is what keeps F1's restore-epoch consume-once a single commit.
 *
 * The five layer BYTE refs and `bgImageRef` stay declared in `MaskEditor.tsx` and are passed in:
 * `useMatte` (J-7) and `useMockupBake` (J-8) are called BEFORE this hook and read them, which is
 * the very reason the file kept bytes in refs rather than state. `setDisplayImage` is published
 * back through `setDisplayImageRef` for the same reason.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { FitMode, VarnishParams } from "@genz/engine-bind";
import type { AppLogger } from "../app/context.js";
import type { EditorRefs } from "./editorContext.js";
import type { ComposeReason, MaskEditorProps, MatteKind } from "./maskEditorTypes.js";
import { loadUncached } from "./uncachedImage.js";
import { dataUrlToBytes, get2dContext } from "./pixels.js";

/** Default spot-UV varnish calibration (mirrors the engine `VarnishParams` defaults). */
export const VARNISH_DEFAULT: Required<VarnishParams> = {
  intensity: 0.74, soften: 0.46, depth: 0.88, gloss: 0.84, embossScale: 1, colorPreserve: 0,
  lightDeg: 140, gradTop: 1, gradBottom: 0.13, surfaceGloss: 0.17,
  grainScale: 0.25, grainOpacity: 0.5, grainContrast: 0.45, grainGradient: 1,
  grainBevel: 0.85, grainRound: 0.91,
  outlineBevel: 0.46, outlineShadow: 0.28, outlineWidth: 0.006, bevelCurve: 0.8,
  gradAngle: 123, dropShadow: 0, dropShadowDist: 0.03, shadowSkew: 0, outlineSmooth: 0.24,
  threshold: 30, thresholdHigh: 255, selectInvert: true, engrave: false,
  highlightColor: [255, 255, 255], shadowColor: [0, 0, 0],
};
import { type ProductCategory } from "./compositing.js";
import { templateLight } from "../stores/segmenterCalibration.js";
import { getTestDesign, putTestDesign, clearTestDesign } from "../stores/testDesignStore.js";
import { fetchWithTimeout, DOWNLOAD_TIMEOUT_MS, NO_HTTP_CACHE } from "../templates/timeout.js";

export interface UseLayerAssetsArgs {
  // --- props the layer code reads ---
  locked?: boolean;
  logger?: AppLogger;
  templateId?: string;
  caseImageUrl?: string;
  onUploadImage?: MaskEditorProps["onUploadImage"];
  initialLayerRefs?: MaskEditorProps["initialLayerRefs"];
  /** The geometric view being authored (B4). A CHANGE resets the per-view layer state (F-35). */
  view?: string;
  /** The nine shared refs (J-3) — ED-6's `layerUploadRef`, F1's `restoreEpochRef`. */
  editorRefs: EditorRefs;

  // --- the layer BYTE refs, declared in `MaskEditor.tsx` because J-7/J-8 read them first ---
  testDesignRef: React.RefObject<Uint8Array | null>;
  clearBgRef: React.RefObject<Uint8Array | null>;
  textureRef: React.RefObject<Uint8Array | null>;
  clearBgUrlRef: React.RefObject<string | null>;
  textureUrlRef: React.RefObject<string | null>;
  bgImageRef: React.RefObject<HTMLImageElement | null>;

  // --- late-bound bridges ---
  /** `useMockupBake` (J-8) is called first; every layer handler re-previews through this. */
  previewMockupRef: React.RefObject<(uploadBake?: boolean, reason?: ComposeReason) => Promise<boolean>>;
  /** Published back so `useMockupBake`'s compose can repaint through ED-7's revoke. */
  setDisplayImageRef: React.RefObject<(url: string | undefined, isObjectUrl?: boolean) => void>;
  /** From `useEditorHistoryBridge` (J-5) — ED-8's stash and ED-9's quiet window. */
  pendingHistoryTargetRef: React.RefObject<unknown>;
  undoneMatteRef: React.RefObject<Uint8Array | Uint8ClampedArray | null>;
  undoneMatteKindRef: React.RefObject<MatteKind | null>;
  resetBackgroundRef: React.RefObject<(() => void) | null>;
  /** From `useMatte` (J-7). */
  matteKindRef: React.RefObject<MatteKind | null>;
  matteClearedRef: React.RefObject<boolean>;
  /** From `useMockupBake` (J-8) — a revert clears the last compose's readout. */
  setAiStatus: (v: null) => void;

  setPlacementMsg: (m: string | null) => void;
  setRemoved: (v: boolean) => void;
  setRemovalVer: (v: number | ((p: number) => number)) => void;

  // --- every value the layer-refs restore effect COMPARES ---
  textureBlend: string;
  varnish: VarnishParams;
  designRotation: number;
  fillFit: FitMode;
  designAspect: [number, number] | null;
  layerOpacity: { phone: number; design: number; texture: number };
  profileCategory: ProductCategory;
  profileClear: boolean;
  profileTexture: boolean;
  printShadingStrength: number;
  shadeGradient: number;
  shadeGrain: number;
  shadeSheen: number;
  shadeHighlights: number;
  shadeShadowDepth: number;
  shadeHighlightDepth: number;
  shadeInkBlack: number;
  shadeInkWhite: number;
  stageDespill: boolean;
  stageRefine: boolean;
  stageMatteStrength: boolean;
  stageShading: boolean;
  matteQuality: "fast" | "quality";
  markerColor: [number, number, number] | null;
  markerTol: number;
  protectProduct: boolean;

  // --- and every setter it WRITES ---
  setTextureBlend: (v: string) => void;
  setVarnish: (v: VarnishParams) => void;
  setDesignRotation: (v: number) => void;
  setFillFit: (v: FitMode) => void;
  setDesignAspect: (v: [number, number] | null) => void;
  setLayerOpacity: React.Dispatch<React.SetStateAction<{ phone: number; design: number; texture: number }>>;
  setProfileCategory: (v: ProductCategory) => void;
  setProfileClear: (v: boolean) => void;
  setProfileTexture: (v: boolean) => void;
  setPrintShadingStrength: (v: number) => void;
  setShadeGradient: (v: number) => void;
  setShadeGrain: (v: number) => void;
  setShadeSheen: (v: number) => void;
  setShadeHighlights: (v: number) => void;
  setShadeShadowDepth: (v: number) => void;
  setShadeHighlightDepth: (v: number) => void;
  setShadeInkBlack: (v: number) => void;
  setShadeInkWhite: (v: number) => void;
  setStageDespill: (v: boolean) => void;
  setStageRefine: (v: boolean) => void;
  setStageMatteStrength: (v: boolean) => void;
  setStageShading: (v: boolean) => void;
  setMatteQuality: (v: "fast" | "quality") => void;
  setMarkerColor: (v: [number, number, number] | null) => void;
  setMarkerTol: (v: number) => void;
  setProtectProduct: (v: boolean) => void;
  setBgMode: (v: "transparent" | "color" | "image") => void;
  setAiEdge: (v: boolean) => void;
}

export function useLayerAssets(args: UseLayerAssetsArgs) {
  const {
    locked, logger, templateId, caseImageUrl, onUploadImage, view,
    initialLayerRefs, editorRefs, testDesignRef, clearBgRef, textureRef,
    clearBgUrlRef, textureUrlRef, bgImageRef, previewMockupRef, setDisplayImageRef,
    pendingHistoryTargetRef, undoneMatteRef, undoneMatteKindRef, resetBackgroundRef, matteKindRef,
    matteClearedRef, setAiStatus, setPlacementMsg, setRemoved, setRemovalVer,
    textureBlend, varnish, designRotation, fillFit, designAspect,
    layerOpacity, profileCategory, profileClear, profileTexture, printShadingStrength,
    shadeGradient, shadeGrain, shadeSheen, shadeHighlights, shadeShadowDepth, shadeHighlightDepth, shadeInkBlack, shadeInkWhite,
    stageDespill, stageRefine,
    stageMatteStrength, stageShading, matteQuality, markerColor, markerTol,
    protectProduct, setTextureBlend, setVarnish, setDesignRotation, setFillFit,
    setDesignAspect, setLayerOpacity, setProfileCategory, setProfileClear, setProfileTexture,
    setPrintShadingStrength, setShadeGradient, setShadeGrain, setShadeSheen, setShadeHighlights,
    setShadeShadowDepth, setShadeHighlightDepth, setShadeInkBlack, setShadeInkWhite, setStageDespill,
    setStageRefine, setStageMatteStrength, setStageShading, setMatteQuality, setMarkerColor,
    setMarkerTol, setProtectProduct, setBgMode, setAiEdge,
  } = args;

  /** TEST DESIGN (testing-only stand-in artwork) — encoded bytes composited into the print
   *  area by "Preview mockup". Persisted with the template (testDesignStore) when templateId
   *  is set. Lets an admin/customer eyeball the live placement before publishing. */
  const [testDesign, setTestDesign] = useState<Uint8Array | null>(null);
  const [testDesignName, setTestDesignName] = useState<string>("");
  const testDesignInputRef = useRef<HTMLInputElement | null>(null);
  /** Clear-case PHONE background (shown behind the design through a clear shell) + product
   *  TEXTURE/effect overlay — images composited into the live mockup so the operator can see
   *  the full result. Uploaded directly here; an admin-curated picker can supply them too. */
  const [clearBg, setClearBg] = useState<Uint8Array | null>(null);
  const [clearBgName, setClearBgName] = useState<string>("");
  const clearBgInputRef = useRef<HTMLInputElement | null>(null);
  const [texture, setTexture] = useState<Uint8Array | null>(null);
  const [textureName, setTextureName] = useState<string>("");
  const textureInputRef = useRef<HTMLInputElement | null>(null);
  // J-13: `useCallback` so the saved-test-design effect below can name them. Bodies unchanged;
  // both of a helper's captures (the ref object and a `useState` setter) are already stable, so the
  // identities never actually change and nothing re-runs that did not re-run before.
  const applyTestDesign = useCallback((b: Uint8Array | null) => { testDesignRef.current = b; setTestDesign(b); }, [testDesignRef]);
  const applyClearBg = useCallback((b: Uint8Array | null) => { clearBgRef.current = b; setClearBg(b); }, [clearBgRef]);
  const applyTexture = useCallback((b: Uint8Array | null) => { textureRef.current = b; setTexture(b); }, [textureRef]);
  /** Composited image data-URL shown in the editor (else the original case image). */
  const [displayUrl, setDisplayUrl] = useState<string | undefined>(undefined);
  /**
   * ED-7 — the last BLOB url handed to `displayUrl`. Every layered compose minted a full-res PNG
   * object URL and none were ever revoked, so a calibration session leaked one decoded PNG per
   * compose (tens of MB each) for the lifetime of the document. This holds the outstanding one so
   * `setDisplayImage` can revoke the previous URL on every swap, and the unmount effect can revoke
   * the last one. Data-URL displays (the recomposite path) store null — nothing to revoke.
   */
  const displayObjectUrlRef = useRef<string | null>(null);
  /** Set the displayed image, revoking the object URL it replaces (ED-7). Pass `isObjectUrl` for
   *  a `URL.createObjectURL` result so it is tracked for revocation. */
  // MN-11 / review-1 M-3: `useCallback` so callers can LIST it. It closes over a ref and a
  // `useState` setter — both stable for the life of the editor — so a stable identity is the
  // honest one, and MaskEditor's recomposite effect can then name it without re-firing.
  const setDisplayImage = useCallback((url: string | undefined, isObjectUrl = false) => {
    const prev = displayObjectUrlRef.current;
    if (prev && prev !== url && typeof URL !== "undefined") {
      try { URL.revokeObjectURL(prev); } catch { /* best-effort */ }
    }
    displayObjectUrlRef.current = isObjectUrl ? (url ?? null) : null;
    setDisplayUrl(url);
  }, []);
  setDisplayImageRef.current = setDisplayImage;
  // Revoke the outstanding display blob when the editor closes (tab switch, force-sync remount…).
  useEffect(() => () => {
    const last = displayObjectUrlRef.current;
    displayObjectUrlRef.current = null;
    if (last && typeof URL !== "undefined") {
      try { URL.revokeObjectURL(last); } catch { /* best-effort */ }
    }
  }, []);

  /** Undo a chroma removal: repaint the ORIGINAL case image onto the source canvas
   *  and drop the removed/preview state (the "Restore background" / revert path). */
  const caseImageUrlRef = useRef(caseImageUrl);
  caseImageUrlRef.current = caseImageUrl;
  const resetBackground = () => {
    if (locked) return;
    logger?.log("info", "Editor · Restore background (reverse auto background removal) requested");
    const canvas = editorRefs.sourceRef.current;
    const ctx = canvas ? get2dContext(canvas) : null;
    const url = caseImageUrl;
    if (!canvas || !ctx || !url || typeof Image === "undefined") {
      // Nothing will change → release the history quiet window (ED-9) so later edits still commit.
      pendingHistoryTargetRef.current = null;
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    // The uncached reload is slow enough for the operator to switch photo before it lands.
    const stale = () => {
      if (caseImageUrlRef.current === url) return false;
      pendingHistoryTargetRef.current = null;
      return true;
    };
    img.onload = () => {
      if (stale()) return;
      try {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setRemoved(false);
        setRemovalVer(0);
        setDisplayImage(undefined);
        // The matte belongs to the removal we just reverted — drop it (and turn AI edge off)
        // so AI edge can't reference a stale matte that no longer matches the restored image.
        // STASH it first: the opposite history direction (redo the removal) has to replay it,
        // and the reference is the only copy we hold (ED-8).
        undoneMatteRef.current = editorRefs.matteAlphaRef.current ?? undoneMatteRef.current;
        undoneMatteKindRef.current = matteKindRef.current ?? undoneMatteKindRef.current;
        editorRefs.matteAlphaRef.current = null;
        matteKindRef.current = null;
        // DELIBERATE clear: mark it so the next bake drops the saved matte instead of carrying it
        // forward (a matte-less bake defaults to carry-forward to survive an incidental re-bake).
        matteClearedRef.current = true;
        setAiEdge(false);
        setAiStatus(null);
        setPlacementMsg("Background restored.");
        logger?.log("info", "Editor · Background restored — auto removal reversed, original image back");
      } catch {
        /* leave as-is on an unreadable canvas */
        pendingHistoryTargetRef.current = null;
      }
    };
    // A 404/CORS here made "Restore background" a silent no-op — say why nothing happened.
    img.onerror = () => {
      if (stale()) return;
      // The removal state never changed, so the history transition can't converge — release the
      // quiet window (ED-9) or every later edit would stop being recorded.
      pendingHistoryTargetRef.current = null;
      logger?.log("error", "Editor · Restore background failed — the original case image could not be re-loaded from hosting (404/CORS)");
      setPlacementMsg("Couldn't restore — the original image failed to load from hosting (404/CORS).");
    };
    void loadUncached(img, url);
  };
  resetBackgroundRef.current = resetBackground;

  // Load any TEST DESIGN saved with this template so the live mockup preview is reproducible
  // across sessions (testing-only; bytes stay on the device).
  useEffect(() => {
    if (!templateId) return;
    let alive = true;
    void getTestDesign(templateId).then((d) => {
      if (!alive || !d) return;
      applyTestDesign(d.design);
      setTestDesignName(d.name);
    });
    return () => { alive = false; };
    // J-13: complete — `applyTestDesign` is a `useCallback` above and `setTestDesignName` is a
    // `useState` setter, so neither can re-trigger the fetch.
  }, [templateId, applyTestDesign]);

  /** Load an uploaded background image and recomposite. */
  const onBgImageFile = (file: File | null | undefined) => {
    if (!file || typeof FileReader === "undefined" || typeof Image === "undefined") return;
    const reader = new FileReader();
    reader.onload = () => {
      const im = new Image();
      im.onload = () => { bgImageRef.current = im; setBgMode("image"); setRemovalVer((v) => v + 1); };
      im.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  };

  /** Upload a TEST DESIGN — stand-in artwork that fills the print area in "Preview mockup".
   *  Persisted with the template (when templateId is set) so it's there next session. */
  const onTestDesignFile = (file: File | null | undefined) => {
    if (!file || typeof FileReader === "undefined") return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const bytes = dataUrlToBytes(String(reader.result));
        applyTestDesign(bytes);
        setTestDesignName(file.name);
        if (templateId) void putTestDesign(templateId, { design: bytes, name: file.name });
        logger?.log("info", `Editor · Test design uploaded (${file.name}) — previewing mockup`);
        void previewMockupRef.current(); // show it in the live mockup immediately
      } catch { /* unreadable image */ }
    };
    reader.readAsDataURL(file);
  };

  /** Forget the test design (memory + persisted) — reverts "Preview mockup" to the empty print area. */
  const clearTestDesignNow = () => {
    applyTestDesign(null);
    setTestDesignName("");
    if (templateId) void clearTestDesign(templateId);
    logger?.log("info", "Editor · Test design removed");
  };

  /** Read an uploaded image into PNG bytes, set it, and re-preview the mockup. Shared by the
   *  clear-case background + texture pickers (both are product fill layers). */
  const onProductImageFile = (
    file: File | null | undefined,
    set: (b: Uint8Array | null) => void,
    setName: (n: string) => void,
    label: string,
    urlRef?: React.MutableRefObject<string | null>,
  ) => {
    if (!file || typeof FileReader === "undefined") return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const bytes = dataUrlToBytes(String(reader.result));
        set(bytes);
        setName(file.name);
        if (urlRef) urlRef.current = null; // local until the R2 upload resolves
        logger?.log("info", `Editor · ${label} uploaded (${file.name}) — previewing mockup`);
        // ED-6: when a hosting upload is about to start, this first compose is DISPLAY-ONLY —
        // the url isn't known yet, so a cloud write here would persist (and erase) the layer
        // ref. The re-bake in the upload's `.then` below performs the real upload-bake.
        const willHost = !!(urlRef && onUploadImage);
        void previewMockupRef.current(!willHost);
        // NO LOCAL-ONLY: push the custom layer to hosting (R2) so it syncs to every system
        // like a library pick (the user: "no local, always save in cloud/R2"). Best-effort.
        if (urlRef && onUploadImage) {
          // ED-6: publish this upload so `bakeNow` can WAIT for it. Save landing mid-upload used
          // to persist `layerRefs` with no url — and since the cloud persist is a full overwrite,
          // that ERASED the previously-saved texture url, with the re-bake retry arriving only
          // after the version had locked (409, unrecoverable).
          const upload = onUploadImage(bytes)
            .then(async (url) => {
              if (!url) {
                // A null return is the caller's "hosting can't take it" signal (file://
                // local hosting). The layer works HERE but will be MISSING on every other
                // device — this used to be completely silent (sync audit: bug class #1).
                logger?.log("warn", `Editor · ${label} could not be published to hosting — the layer stays on THIS device only and other systems will render without it`);
                setPlacementMsg(`${label} saved locally, but hosting refused it — other systems will render WITHOUT this layer.`);
                return;
              }
              urlRef.current = url;
              // RE-BAKE once the hosted URL exists: the upload-bake fired above RACED this
              // upload and persisted `layerRefs` WITHOUT the url (refs aren't in the dirty
              // deps, so nothing else would ever re-emit it) — other devices then hydrated a
              // mockup with no texture/clear-bg layer. This second upload-bake persists the
              // url; it's cheap next to the R2 upload that just finished. (Sync audit #2.)
              logger?.log("info", `Editor · ${label} hosted — re-baking so the layer URL syncs`);
              // LIFT the in-flight guard FIRST. `composeMockupOnce` downgrades an upload-bake to
              // a display-only compose while `layerUploadRef` is set (ED-6, so a Save mid-upload
              // can't persist refs with no url and erase the saved one). That guard was still
              // armed here — it is only cleared by the `.finally` BELOW, which runs after this
              // handler — so this corrective bake, the one whose whole job is to persist the url
              // that just resolved, was itself downgraded and emitted nothing. It survived only
              // because the downgrade marks the bake dirty, so a LATER Save re-baked; picking a
              // layer and closing without saving lost the url entirely. The url is resolved now,
              // so the guard has nothing left to protect.
              editorRefs.layerUploadRef.current = null;
              await previewMockupRef.current();
            })
            .catch((e) => {
              // Was a bare swallow — the single silent failure that made "template layers
              // missing for the other user" undiagnosable. Loud now, in the editor itself.
              const msg = e instanceof Error ? e.message : String(e);
              logger?.log("error", `Editor · ${label} upload to hosting FAILED (${msg}) — the layer stays on THIS device only; other systems will render without it. Retry by re-picking the image.`);
              setPlacementMsg(`${label} upload failed (${msg}) — other systems will render WITHOUT this layer. Re-pick the image to retry.`);
            });
          editorRefs.layerUploadRef.current = upload;
          void upload.finally(() => { if (editorRefs.layerUploadRef.current === upload) editorRefs.layerUploadRef.current = null; });
        }
      } catch { /* unreadable image */ }
    };
    reader.readAsDataURL(file);
  };

  /** Pick a product image (texture / clear-case background) from the admin LIBRARY: fetch its
   *  bytes, set the layer, and re-preview. An empty url clears the layer. `stillWanted` drops
   *  bytes that arrive after the editor moved on. */
  const pickImageFromUrl = async (
    url: string,
    set: (b: Uint8Array | null) => void,
    setName: (n: string) => void,
    name: string,
    label: string,
    stillWanted: () => boolean = () => true,
  ) => {
    if (!url) {
      set(null);
      setName("");
      void previewMockupRef.current();
      return;
    }
    try {
      const res = await fetchWithTimeout(url, NO_HTTP_CACHE, DOWNLOAD_TIMEOUT_MS, "Layer image download");
      if (!res.ok) throw new Error(`status ${res.status}`);
      const buf = new Uint8Array(await res.arrayBuffer());
      if (buf.length === 0) throw new Error("empty image");
      if (!stillWanted()) return;
      set(buf);
      setName(name);
      logger?.log("info", `Editor · ${label} selected from library (${name})`);
      void previewMockupRef.current();
    } catch (e) {
      setPlacementMsg(`${label} couldn’t be loaded from the library.`);
      logger?.log("error", `Editor · ${label} library fetch failed — ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Restore texture / clear-case-background LAYERS from a previously-baked version (which
  // may have been built on ANOTHER system) so they appear in the editor here too — fixing
  // "layers are missing when I open the template on a different system". Runs once; the
  // hosted library URL is re-fetched → bytes. Never clobbers an in-session pick.
  /**
   * MN-3 / J-13 — the STABLE identity of `initialLayerRefs`. See the same constant in `useMatte`:
   * keying on the VALUE makes "the saved refs changed" mean what it says, without touching
   * `TemplateEditor.tsx` (WS-K2 owns that file).
   */
  const layerRefsKey = JSON.stringify(initialLayerRefs ?? null);
  const restoredLayersRef = useRef(false);
  /**
   * MN-11 / MN-3 / J-13 — the restore BODY, held in a ref so the effect can name a complete
   * dependency list.
   *
   * The body reads ~15 setters and three helpers that are all redefined every render, plus a
   * dozen live values it COMPARES (`restoreChangesDirtyDep`). Listing them would re-run the
   * restore on every appearance change; omitting them was the lie the suppression hid. A ref says
   * the true thing: the effect wants the CURRENT body, and the only thing that should TRIGGER it
   * is the saved refs actually changing. Body unchanged, comments included.
   */
  const runLayerRestoreRef = useRef<() => void>(() => {});
  /** The view on screen now: a restore's bytes land only on the view it started for. */
  const viewRef = useRef(view);
  viewRef.current = view;
  runLayerRestoreRef.current = () => {
    const refs = initialLayerRefs;
    const forView = view;
    const sameView = () => viewRef.current === forView;
    if (!refs || restoredLayersRef.current) return;
    restoredLayersRef.current = true;
    void (async () => {
      try {
        // These restored transforms/toggles (textureBlend / designRotation / fillFit / designAspect /
        // profileTexture / profileClear) are ALL bake-dirty appearance deps, so replaying a saved
        // template's layer state would trip the dirty effect on reopen → Save re-uploads identical
        // assets. Suppress that with the SAME epoch handshake the matte restore uses (F1): bump the
        // restore epoch ONCE, and only when a set will actually CHANGE a dirty dep (else React bails
        // the no-op set, the dirty effect never runs, the epoch is never consumed, and the user's
        // NEXT real edit would be wrongly swallowed). All the appearance-dep sets are applied in this
        // ONE synchronous batch (before any await) so they land in a single commit → one dirty-effect
        // run → one consume; the profile toggles precede their pickImageFromUrl exactly as before.
        const wantTexture = !!(refs.textureUrl && !textureRef.current);
        const wantClear = !!(refs.clearBgUrl && !clearBgRef.current);
        // The texture LAYER's enabled state. BI-DIRECTIONAL (audit P1-4): an explicitly saved flag
        // wins in BOTH directions, so a stored `false` actually turns the layer OFF. The previous
        // `refs.textureOn === true || wantTexture` form could only ever turn it ON, which made a
        // stored `false` unreachable — the next Save then wrote `true` straight back, so the
        // operator could never persist the layer as off.
        //
        // With the flag ABSENT (records saved before it existed) the legacy behaviour is preserved
        // EXACTLY: the image-derived `wantTexture` can still turn the layer on, and falling back to
        // the CURRENT state otherwise makes the assignment below a no-op — so a base-type-derived
        // seed is never silently forced off by a record that merely predates the flag.
        const wantTextureOn = typeof refs.textureOn === "boolean"
          ? refs.textureOn
          : (wantTexture || profileTexture);
        // Same rule for the clear-case layer (see `clearOn` in the prop type). This one was the
        // reported bug: the mount seed is `true` for clear/glass base types, so a one-way restore
        // left "Clear" permanently stuck on.
        const wantClearOn = typeof refs.clearOn === "boolean"
          ? refs.clearOn
          : (wantClear || profileClear);
        // The product CATEGORY (P1-3). Saved value wins; absent keeps the baseType-derived seed.
        const targetCategory = (refs.productCategory as ProductCategory | undefined) ?? profileCategory;
        // E2 compat: an EXISTING template (refs present) whose refs LACK design_fill was saved
        // before the fill control existed → it must keep behaving as "height", NOT inherit the new
        // "stretch" default the useState seeds for brand-new templates. So the restore target is the
        // saved value when present, else "height". (A new template mounts WITHOUT initialLayerRefs,
        // so this effect early-returns above and the "stretch" seed stands.)
        const targetFill: FitMode = refs.fillFit ?? "height";
        // The light the template has NOW: its own, or the platform's when "apply to every
        // template" ran after it was saved. The sliders show what renders.
        const light = templateLight(refs.shading);
        const savedStrength = light && light.strength !== refs.shading?.strength ? light.strength : refs.printShadingStrength;
        const restoreChangesDirtyDep =
          (!!refs.textureBlend && refs.textureBlend !== textureBlend) ||
          (!!refs.varnish && JSON.stringify({ ...VARNISH_DEFAULT, ...refs.varnish }) !== JSON.stringify(varnish)) ||
          (typeof refs.designRotation === "number" && refs.designRotation !== designRotation) ||
          (targetFill !== fillFit) ||
          (!!refs.designAspect && refs.designAspect !== designAspect) ||
          // P1-4: compare BOTH directions now that the restore can also turn a layer off — an
          // `on → off` restore is just as much a dirty-dep change as `off → on`.
          (wantTextureOn !== profileTexture) ||
          (wantClearOn !== profileClear) ||
          (targetCategory !== profileCategory) ||
          (!!refs.markerColor && JSON.stringify(refs.markerColor) !== JSON.stringify(markerColor)) ||
          (typeof refs.markerTol === "number" && refs.markerTol !== markerTol) ||
          (typeof refs.protectProduct === "boolean" && refs.protectProduct !== protectProduct) ||
          (typeof light?.sheen === "number" && light.sheen !== shadeSheen) ||
          // WS-HL: `highlights` uses the PRESENCE of the saved object, not of the key — a
          // template saved before this feature existed had no additive highlights, so the
          // restore target is 0, not the new-template seed (see the setter below).
          (!!light && (light.highlights ?? 0) !== shadeHighlights) ||
          (typeof light?.shadowBoost === "number" && light.shadowBoost !== shadeShadowDepth) ||
          (typeof light?.highlightBoost === "number" && light.highlightBoost !== shadeHighlightDepth) ||
          (typeof light?.inkBlack === "number" && light.inkBlack !== shadeInkBlack) ||
          (typeof light?.inkWhite === "number" && light.inkWhite !== shadeInkWhite) ||
          (typeof refs.opacity?.design === "number" && refs.opacity.design !== layerOpacity.design) ||
          (typeof refs.opacity?.texture === "number" && refs.opacity.texture !== layerOpacity.texture) ||
          (typeof refs.opacity?.phone === "number" && refs.opacity.phone !== layerOpacity.phone) ||
          // `matteQuality` IS a bake-dirty dep and IS restored here, but it was missing from this
          // chain — so merely OPENING a template saved as "quality" (the mount seed is the default)
          // changed a dirty dep without bumping the restore epoch. The dirty effect then ran
          // unsuppressed and marked an untouched template dirty on open, which made the next Save
          // re-upload byte-identical assets and rotate the version token for nothing.
          (!!refs.matteQuality && refs.matteQuality !== matteQuality) ||
          (typeof savedStrength === "number" && savedStrength !== printShadingStrength) ||
          (typeof light?.gradient === "number" && light.gradient !== shadeGradient) ||
          (typeof light?.grain === "number" && light.grain !== shadeGrain) ||
          (typeof refs.stageDespill === "boolean" && refs.stageDespill !== stageDespill) ||
          (typeof refs.stageRefine === "boolean" && refs.stageRefine !== stageRefine) ||
          (typeof refs.stageMatteStrength === "boolean" && refs.stageMatteStrength !== stageMatteStrength) ||
          (typeof refs.stageShading === "boolean" && refs.stageShading !== stageShading);
        if (restoreChangesDirtyDep) editorRefs.restoreEpochRef.current += 1;
        if (refs.textureBlend) setTextureBlend(refs.textureBlend);
        if (refs.varnish) setVarnish({ ...VARNISH_DEFAULT, ...refs.varnish });
        if (typeof refs.designRotation === "number") setDesignRotation(refs.designRotation);
        setFillFit(targetFill);
        if (refs.designAspect) setDesignAspect(refs.designAspect);
        if (wantTexture) textureUrlRef.current = refs.textureUrl!;
        // P1-4: assign the resolved value rather than only ever setting `true` — see `wantTextureOn`.
        setProfileTexture(wantTextureOn);
        if (wantClear) clearBgUrlRef.current = refs.clearBgUrl!;
        setProfileClear(wantClearOn);
        // P1-3: the saved product category (a "Mug" pick used to be re-seeded away from baseType,
        // which also re-enabled the clear phone layer on reopen).
        setProfileCategory(targetCategory);
        // P1-1 / P1-2: the marker keying colour + tolerance and "Protect product". These feed the
        // keyer, so without restoring them the next dirty Save re-keys with the DEFAULTS and
        // uploads a wrong shell + print mask. Absent ⇒ keep the mount seed (default magenta / on).
        if (refs.markerColor) setMarkerColor(refs.markerColor);
        if (typeof refs.markerTol === "number") setMarkerTol(refs.markerTol);
        if (typeof refs.protectProduct === "boolean") setProtectProduct(refs.protectProduct);
        // Per-layer opacity: persisted and honoured by the RENDER all along, but the editor
        // always mounted at 100%, so reopening and saving overwrote a calibrated value with 1.0.
        if (refs.opacity) {
          setLayerOpacity((prev) => ({
            design: refs.opacity?.design ?? prev.design,
            texture: refs.opacity?.texture ?? prev.texture,
            phone: refs.opacity?.phone ?? prev.phone,
          }));
        }
        // Artwork shading + per-stage pipeline toggles + matte quality (AI settings popup) — same
        // synchronous batch so the epoch handshake above suppresses the spurious dirty on reopen.
        if (typeof savedStrength === "number") setPrintShadingStrength(savedStrength);
        // Synthetic-light sliders ride in the saved shading object; templates saved before the
        // feature lack it and keep the calibrated defaults seeded by useState.
        if (typeof light?.gradient === "number") setShadeGradient(light.gradient);
        if (typeof light?.grain === "number") setShadeGrain(light.grain);
        // Gloss sheen rides the same saved object but was NEVER read back — a calibrated
        // value round-tripped to the cloud and then silently reverted to the default on
        // reopen, so the operator could never actually change it (audit A3).
        if (typeof light?.sheen === "number") setShadeSheen(light.sheen);
        // Additive highlights (WS-HL). Deliberately NOT keyed on the FIELD being present like
        // its three neighbours: a template saved before this feature has a `shading` object
        // WITHOUT `highlights`, and it rendered with none. Keeping the mount seed there (40%,
        // the NEW-template default) would silently re-light every existing template on reopen
        // and persist that on the next Save. An existing calibration therefore restores to its
        // stored value, or to 0 when it has none; only a template with NO saved calibration at
        // all keeps the seed.
        if (light) setShadeHighlights(light.highlights ?? 0);
        // Every calibration saved since these were engine constants carries both, at the
        // shipped values; one that carries neither keeps the mount seed, which is the same.
        if (typeof light?.shadowBoost === "number") setShadeShadowDepth(light.shadowBoost);
        if (typeof light?.highlightBoost === "number") setShadeHighlightDepth(light.highlightBoost);
        if (typeof light?.inkBlack === "number") setShadeInkBlack(light.inkBlack);
        if (typeof light?.inkWhite === "number") setShadeInkWhite(light.inkWhite);
        if (typeof refs.stageDespill === "boolean") setStageDespill(refs.stageDespill);
        if (typeof refs.stageRefine === "boolean") setStageRefine(refs.stageRefine);
        if (typeof refs.stageMatteStrength === "boolean") setStageMatteStrength(refs.stageMatteStrength);
        if (typeof refs.stageShading === "boolean") setStageShading(refs.stageShading);
        if (refs.matteQuality) setMatteQuality(refs.matteQuality);
        // Fetch the layer bytes AFTER the synchronous state batch above (each awaits hosting).
        if (wantTexture) {
          await pickImageFromUrl(refs.textureUrl!, applyTexture, setTextureName, "Texture (synced)", "Texture", sameView);
        }
        if (wantClear) {
          await pickImageFromUrl(refs.clearBgUrl!, applyClearBg, setClearBgName, "Background (synced)", "Clear-case background", sameView);
        }
      } catch (e) {
        // Silent-failure sweep: this swallowed everything, so the texture / clear-case layer,
        // the opacity and the shading round-trip simply never came back — and the next Save
        // FULL-OVERWRITES the cloud refs with the un-restored state (release-audit classes 6
        // and 10).
        //
        // WS-ERR F-17: the fix for that is the REPORT, not an un-latch. Clearing
        // `restoredLayersRef` here looked like a retry and was not one — this effect is keyed
        // on `layerRefsKey` (the refs' VALUE), which does not change because a download failed,
        // so nothing would ever re-enter and the cleared latch only meant the NEXT unrelated
        // re-key would run the restore twice. The real retry is Force sync, which re-pulls the
        // record and REMOUNTS the editor, and that is what the message names.
        //
        // The dirty state is safe: `restoreEpochRef` was bumped and the whole synchronous
        // setState batch ran BEFORE the awaits that can throw, so the dirty effect is
        // suppressed exactly as it is on a successful restore. An untouched template is not
        // marked dirty by a failure here, which is what stops a Save the operator did not ask
        // for from uploading half-restored layers.
        logger?.log("error", "Editor · this template's saved layers could not be restored — do NOT save until they are back, or the save will erase them", {
          step: "restoreLayers", templateId, view, error: e,
        });
        setPlacementMsg(
          "Couldn’t restore this template’s saved layers (texture / background). Use Force sync before you save — saving now would erase them.",
        );
      }
    })();
  };
  useEffect(() => {
    runLayerRestoreRef.current();
    // J-13 / MN-3: keyed on the refs' VALUE, not their identity. `TemplateEditor` rebuilds
    // `initialLayerRefs` fresh every render, so this used to run on every keystroke in the parent
    // and was held together only by `restoredLayersRef`. The one-shot guard stays; it is no
    // longer what stops a per-render flood.
  }, [layerRefsKey]);

  /**
   * F-35 / J-14 — reset the per-view layer state on a `view` prop change instead of a remount.
   * Declared empty by J-9 so F-35 lands as one small diff against a settled seam.
   */
  const resetForView = () => {
    // ED-7: routing the clear through `setDisplayImage` is what revokes the outgoing view's blob.
    setDisplayImage(undefined);
    setPlacementMsg(null);
    setAiStatus(null);
    // Re-arm the layer restore for the NEW view's refs. (`layerRefsKey` changes per view too, so
    // this is belt-and-braces — but the guard is what the restore actually reads.)
    restoredLayersRef.current = false;
    // NOT reset, by design (F-35 names them): the loaded TEST DESIGN and the background-replace
    // choice both belong to the operator's session, not to the view.
  };
  /** Always the latest `resetForView` closure, so the view effect below can list only `view`. */
  const resetForViewRef = useRef(resetForView);
  resetForViewRef.current = resetForView;
  /**
   * F-35 / J-14 — run `resetForView` on a `view` CHANGE, never on the first render: the mount has
   * already produced exactly this state, and re-running it would undo the mount's own work.
   */
  const seenViewRef = useRef<string | undefined>(view);
  useEffect(() => {
    if (seenViewRef.current === view) return;
    seenViewRef.current = view;
    resetForViewRef.current();
  }, [view]);


  return {
    testDesign, testDesignName, setTestDesignName, testDesignInputRef,
    clearBg, clearBgName, setClearBgName, clearBgInputRef,
    texture, textureName, setTextureName, textureInputRef,
    applyTestDesign, applyClearBg, applyTexture,
    displayUrl, setDisplayImage,
    onBgImageFile, onTestDesignFile, clearTestDesignNow, onProductImageFile, pickImageFromUrl,
    resetBackground,
    resetForView,
  };
}
